import { useState } from "react";
import { motion } from "framer-motion";
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../utils/cn";
import { useToast } from "../contexts/ToastContext";
import { useSettings } from "../contexts/SettingsContext";
import { EXTERNAL_LINKS } from "../constants/links";

const navigation = {
  product: [
    { name: "Features", href: "#features" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "Institutions", href: "#institutions" },
    { name: "Documentation", href: "https://de5.gitbook.io/de5-docs/" },
  ],
  company: [
    { name: "Blog", href: "#blog" },
    { name: "Partner with us", href: "#partners" },
    { name: "Careers", comingSoon: true },
    { name: "Contact", href: EXTERNAL_LINKS.telegram },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms of Service", href: "/terms-of-service" },
    { name: "Security", comingSoon: true },
    { name: "Compliance", comingSoon: true },
  ],
};

const socialLinks = [
  {
    name: "Twitter",
    href: EXTERNAL_LINKS.twitter,
    icon: (props: any) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
      </svg>
    ),
  },
  {
    name: "GitHub",
    href: "https://github.com/De5-ai",
    icon: (props: any) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path
          fillRule="evenodd"
          d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: "Telegram",
    href: "https://t.co/iLVsG7i5e1",
    icon: (props: any) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    ),
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const { settings } = useSettings();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast({
        type: "error",
        title: "Email Required",
        message: "Please enter your email address",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast({
          type: "success",
          title: "Successfully Subscribed!",
          message: "Thank you for subscribing to our updates",
        });
        setEmail("");
      } else {
        showToast({
          type: "error",
          title: "Subscription Failed",
          message: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        title: "Network Error",
        message:
          "Unable to connect. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <footer className="relative bg-transparent">
      {/* Top fade from previous section */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-b from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_bottom,black,transparent)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <h3
                  className="text-2xl font-oxanium font-bold"
                  style={{ color: settings.logoColor }}
                >
                  de5.ai
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                The future of DeFi automation.
              </p>

              <div className="space-y-3">
                <a
                  href={EXTERNAL_LINKS.support}
                  className="flex items-center gap-3 text-gray-600 dark:text-gray-400 transition-colors"
                  style={{ "--hover-color": "#e95c49" } as React.CSSProperties}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#e95c49")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                >
                  <EnvelopeIcon className="h-5 w-5 text-gray-600 dark:text-white" />
                  support@de5.ai
                </a>
                <a
                  href={EXTERNAL_LINKS.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-600 dark:text-gray-400 transition-colors"
                  style={{ "--hover-color": "#e95c49" } as React.CSSProperties}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#e95c49")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-600 dark:text-white" />
                  Live Chat Support
                </a>
              </div>
            </div>

            {/* Navigation sections */}
            <motion.div variants={itemVariants}>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 font-oxanium">
                Product
              </h3>
              <ul className="space-y-3">
                {navigation.product.map((item) => (
                  <li key={item.name}>
                    <motion.a
                      href={item.href}
                      target={
                        item.href.startsWith("http") ? "_blank" : undefined
                      }
                      rel={
                        item.href.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className="text-gray-600 dark:text-gray-400 transition-colors"
                      style={
                        { "--hover-color": "#e95c49" } as React.CSSProperties
                      }
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#e95c49")
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.name}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 font-oxanium">
                Company
              </h3>
              <ul className="space-y-3">
                {navigation.company.map((item) => (
                  <li key={item.name} className="flex items-center gap-2">
                    {item.href ? (
                      <motion.a
                        href={item.href}
                        target={
                          item.href.startsWith("http") ? "_blank" : undefined
                        }
                        rel={
                          item.href.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                        className="text-gray-600 dark:text-gray-400 transition-colors"
                        style={
                          {
                            "--hover-color": "#e95c49",
                          } as React.CSSProperties
                        }
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#e95c49")
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.name}
                      </motion.a>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        {item.name}
                        {item.comingSoon && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full whitespace-nowrap">
                            Coming Soon
                          </span>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 font-oxanium">
                Legal
              </h3>
              <ul className="space-y-3">
                {navigation.legal.map((item) => (
                  <li key={item.name} className="flex items-center gap-2">
                    {item.href ? (
                      <motion.a
                        href={item.href}
                        target={
                          item.href.startsWith("http") ? "_blank" : undefined
                        }
                        rel={
                          item.href.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                        className="text-gray-600 dark:text-gray-400 transition-colors"
                        style={
                          {
                            "--hover-color": "#e95c49",
                          } as React.CSSProperties
                        }
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#e95c49")
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.name}
                      </motion.a>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        {item.name}
                        {item.comingSoon && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full whitespace-nowrap">
                            Coming Soon
                          </span>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Bottom section */}
          <motion.div
            variants={itemVariants}
            className="mt-12 pt-8 border-t border-gray-200 dark:border-dark-700"
          >
            <div className="flex flex-col md:flex-row justify-between items-center gap-8">
              {/* Social bar */}
              <div className="flex items-center gap-6">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Follow us:
                </span>
                <div className="flex gap-3">
                  {socialLinks.map((link) => (
                    <motion.a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-transform"
                      aria-label={link.name}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="sr-only">{link.name}</span>
                      <div
                        className="p-1.5 rounded-full"
                        style={{ backgroundColor: settings.primaryColor }}
                      >
                        <link.icon className="h-4 w-4 text-white" />
                      </div>
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Newsletter inline */}
              <div className="flex flex-col gap-3">
                <form
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                >
                  <label
                    htmlFor="footer-email"
                    className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap"
                  >
                    Stay updated:
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="footer-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      disabled={isSubmitting}
                      className={cn(
                        "px-4 py-2 rounded-lg border border-gray-300 dark:border-dark-600",
                        "bg-white dark:bg-dark-800 text-gray-900 dark:text-white",
                        "focus:ring-2 focus:border-transparent",
                        "placeholder-gray-500 dark:placeholder-gray-400",
                        "text-sm w-48",
                        isSubmitting && "opacity-50 cursor-not-allowed"
                      )}
                    />
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        "px-4 py-2 rounded-lg font-medium text-sm text-white transition-all duration-300",
                        isSubmitting && "opacity-50 cursor-not-allowed"
                      )}
                      style={{
                        backgroundColor: settings.primaryColor,
                        boxShadow: `0 4px 14px 0 ${settings.primaryColor}25`,
                      }}
                      whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                    >
                      {isSubmitting ? "Subscribing..." : "Subscribe"}
                    </motion.button>
                  </div>
                </form>
              </div>
            </div>

            {/* Copyright + status */}
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-dark-700 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-4">
                <span>© 2025 de5.ai. All rights reserved.</span>
                <div className="hidden md:flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: settings.primaryColor }}
                  />
                  <span>All systems operational</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
}
